import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";

const WORKFLOW = [
  { n: "01", title: "Recherche personnalisée", text: "Nous définissons ensemble vos critères réels — mode de vie, budget, secteur — pas seulement une surface." },
  { n: "02", title: "Sélection de biens", text: "Une shortlist qualifiée, incluant des biens off-market de notre réseau, visitée et filtrée pour vous." },
  { n: "03", title: "Organisation des visites", text: "Visites groupées et optimisées, en présentiel ou en visio si vous êtes à distance." },
  { n: "04", title: "Négociation", text: "Nous défendons vos intérêts et négocions le prix et les conditions en votre faveur." },
  { n: "05", title: "Accompagnement notarial", text: "Vérification des documents, coordination avec le notaire et suivi du dossier." },
  { n: "06", title: "Remise des clés", text: "Nous restons à vos côtés jusqu'à la signature — et bien au-delà." },
];

const fieldCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-light text-azur outline-none transition-colors focus:border-azur";

export default function Acheter() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", budget: "", type_bien: "", secteur: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", {
        kind: "achat",
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        details: { budget: form.budget, type_bien: form.type_bien, secteur: form.secteur },
      });
      toast.success("Votre projet a bien été transmis. Un conseiller vous recontacte très vite.");
      setForm({ name: "", email: "", phone: "", budget: "", type_bien: "", secteur: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Acheter"
        title="Vous cherchez votre prochaine adresse ?"
        sub="Nous vous accompagnons dans la recherche du bien qui correspond réellement à votre projet, à vos critères et à votre mode de vie."
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            {WORKFLOW.map((w, i) => (
              <Reveal key={w.n} delay={i * 0.06}>
                <div className="group flex gap-6 border-t border-slate-200 py-7 transition-colors duration-500 hover:border-gold">
                  <span className="outline-number font-serif text-4xl font-light sm:text-5xl">{w.n}</span>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-azur">{w.title}</h3>
                    <p className="mt-1.5 text-sm font-light leading-relaxed text-slate-600">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="buyer-form"
              className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] lg:sticky lg:top-28"
            >
              <span className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-terracotta">
                Parler de mon projet
              </span>
              <h2 className="mt-3 font-serif text-2xl font-semibold text-azur">
                Racontez-nous ce que vous cherchez.
              </h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <input required placeholder="Nom et prénom" value={form.name} onChange={set("name")} data-testid="buyer-name" className={fieldCls} />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="buyer-email" className={fieldCls} />
                <input placeholder="Téléphone" value={form.phone} onChange={set("phone")} data-testid="buyer-phone" className={fieldCls} />
                <select value={form.budget} onChange={set("budget")} data-testid="buyer-budget" className={fieldCls}>
                  <option value="">Budget envisagé</option>
                  <option value="< 1 M€">Moins de 1 M€</option>
                  <option value="1 – 3 M€">1 – 3 M€</option>
                  <option value="3 – 5 M€">3 – 5 M€</option>
                  <option value="5 – 10 M€">5 – 10 M€</option>
                  <option value="> 10 M€">Plus de 10 M€</option>
                </select>
                <select value={form.type_bien} onChange={set("type_bien")} data-testid="buyer-type" className={fieldCls}>
                  <option value="">Type de bien recherché</option>
                  <option value="Villa">Villa</option>
                  <option value="Appartement">Appartement</option>
                  <option value="Maison">Maison</option>
                </select>
                <input placeholder="Secteur souhaité" value={form.secteur} onChange={set("secteur")} data-testid="buyer-sector" className={fieldCls} />
              </div>
              <textarea
                rows={4}
                placeholder="Votre projet en quelques mots (délais, coups de cœur, contraintes…)"
                value={form.message}
                onChange={set("message")}
                data-testid="buyer-message"
                className={fieldCls + " mt-4 resize-none"}
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="buyer-submit"
                className="mt-6 w-full rounded-xl bg-azur px-6 py-4 text-sm font-medium text-sand transition-colors duration-300 hover:bg-terracotta disabled:opacity-60"
              >
                {sending ? "Envoi en cours…" : "Parler de mon projet"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
